/* eslint-disable security/detect-object-injection */
import { msdyn365Commerce } from '@msdyn365-commerce/core';
import { getCartData, getNewCartPosData, setNewCartPosData } from './cookieutils';
import { withoutStoreLineFacts } from './orderstate';

interface CartLineBase {
    ItemId: string;
    ProductId: number;
    InventoryDimensionId?: string;
    Quantity: number;
    Comments: string;
}

interface AddCartLine extends CartLineBase {
    // no LineId
}

interface UpdateCartLine extends CartLineBase {
    LineId: string; // required for update
}

export const buildAddCartLine = (line: CartLineBase): AddCartLine => ({
    ItemId: line.ItemId,
    ProductId: line.ProductId,
    InventoryDimensionId: line.InventoryDimensionId,
    Quantity: line.Quantity,
    Comments: line.Comments
});

export const buildUpdateCartLine = (line: CartLineBase & { LineId: string }): UpdateCartLine => ({
    LineId: line.LineId,
    ItemId: line.ItemId,
    ProductId: line.ProductId,
    InventoryDimensionId: line.InventoryDimensionId,
    Quantity: line.Quantity,
    Comments: line.Comments
});

const isSameId = (left: unknown, right: unknown): boolean => {
    if (left === undefined || left === null || right === undefined || right === null) {
        return false;
    }

    return String(left) === String(right);
};

export const addLineToCartPosData = (newLine: any): void => {
    if (!msdyn365Commerce.isBrowser) {
        return;
    }

    const existingData = getNewCartPosData() || {};
    const currentLines = Array.isArray(existingData.AddCartLines) ? existingData.AddCartLines : [];

    const existingIndex = currentLines.findIndex((l: any) => isSameId(l.ProductId, newLine.ProductId));

    let updatedLines;
    if (existingIndex > -1) {
        updatedLines = [...currentLines];
        updatedLines[existingIndex] = {
            ...updatedLines[existingIndex],
            Quantity: newLine.Quantity,
            Comments: newLine.Comments ?? newLine.Comment,
            ExtensionProperties: newLine.ExtensionProperties ?? updatedLines[existingIndex].ExtensionProperties
        };
    } else {
        updatedLines = [
            ...currentLines,
            {
                ...newLine,
                Comments: newLine.Comments ?? newLine.Comment
            }
        ];
    }

    const updatedData = {
        ...existingData,
        AddCartLines: updatedLines
    };
    setNewCartPosData(updatedData);
};

export const updateLineToCartPosData = (newLine: any): void => {
    if (!msdyn365Commerce.isBrowser) {
        return;
    }

    const existingData = getNewCartPosData() || {};
    const currentLines = Array.isArray(existingData.UpdateCartLines) ? existingData.UpdateCartLines : [];

    const existingIndex = currentLines.findIndex((l: any) => isSameId(l.ProductId, newLine.ProductId));

    let updatedLines;
    if (existingIndex > -1) {
        updatedLines = [...currentLines];
        updatedLines[existingIndex] = {
            ...updatedLines[existingIndex],
            LineId: newLine.LineId, // ✅ ensure LineId is saved
            Quantity: newLine.Quantity,
            Comments: newLine.Comments ?? newLine.Comment
        };
    } else {
        updatedLines = [
            ...currentLines,
            {
                ...newLine,
                LineId: newLine.LineId, // ✅ always add for update
                Comments: newLine.Comments ?? newLine.Comment
            }
        ];
    }

    const updatedData = {
        ...existingData,
        UpdateCartLines: updatedLines
    };

    setNewCartPosData(updatedData);
};

export const removeLineFromCartPosData = (productId?: string | number): void => {
    const existingData = getNewCartPosData() || {};
    const currentLines = Array.isArray(existingData.AddCartLines) ? existingData.AddCartLines : [];

    const updatedLines = currentLines.filter((line: any) => !isSameId(line.ProductId, productId));

    const updatedData = {
        ...existingData,
        AddCartLines: updatedLines
    };

    setNewCartPosData(updatedData);
};

export const removeLineFromUpdateCartPosData = (productId?: string | number): void => {
    if (!msdyn365Commerce.isBrowser) {
        return;
    }

    const existingData = getNewCartPosData() || {};
    const currentLines = Array.isArray(existingData.UpdateCartLines) ? existingData.UpdateCartLines : [];

    const updatedLines = currentLines.filter((line: any) => !isSameId(line.ProductId, productId));

    const updatedData = {
        ...existingData,
        UpdateCartLines: updatedLines
    };

    setNewCartPosData(updatedData);
};

export const removeLineFromUpdateCartPosDataByLineId = (lineId?: string): void => {
    if (!msdyn365Commerce.isBrowser) {
        return;
    }

    const existingData = getNewCartPosData() || {};
    const currentLines = Array.isArray(existingData.UpdateCartLines) ? existingData.UpdateCartLines : [];

    const updatedLines = currentLines.filter((line: any) => !isSameId(line.LineId, lineId));

    const updatedData = {
        ...existingData,
        UpdateCartLines: updatedLines
    };

    setNewCartPosData(updatedData);
};

export const removeLineByLineIdToCookie = (lineId?: string): void => {
    if (!msdyn365Commerce.isBrowser || !lineId) {
        return;
    }

    const existingData = getCartData() || {};
    const currentLines = Array.isArray(existingData.cartLines) ? existingData.cartLines : [];

    // ✅ Look for a matching line by LineId (only remove if it exists in cookie)
    const cookieLine = currentLines.find((line: any) => line.LineId === lineId);

    if (cookieLine) {
        // Line exists in cookie → add to RemoveCartLines
        const newData = getNewCartPosData() || {};
        const currentRemovals = Array.isArray(newData.RemoveCartLines) ? newData.RemoveCartLines : [];

        // Avoid duplicates
        const alreadyExists = currentRemovals.some((line: any) => line.LineId === lineId);

        if (!alreadyExists) {
            const updatedData = {
                ...newData,
                RemoveCartLines: [...currentRemovals, { LineId: lineId }]
            };

            console.log('🔹 Line exists in cookie → added to RemoveCartLines:', updatedData);
            setNewCartPosData(updatedData);
        }
    } else {
        // Line does NOT exist in cookie → make sure it’s NOT in RemoveCartLines
        const newData = getNewCartPosData() || {};
        const currentRemovals = Array.isArray(newData.RemoveCartLines) ? newData.RemoveCartLines : [];

        const updatedRemovals = currentRemovals.filter((line: any) => line.LineId !== lineId);

        const updatedData = {
            ...newData,
            RemoveCartLines: updatedRemovals
        };

        console.log('🔹 Line not in cookie → removed from RemoveCartLines if present:', updatedData);
        setNewCartPosData(updatedData);
    }
};

// export const removeLineByLineIdToCookie = (lineId?: string): void => {
//     if (!msdyn365Commerce.isBrowser) {
//         return;
//     }

//     const existingData = getCartData() || {};
//     const currentLines = Array.isArray(existingData.cartLines) ? existingData.cartLines : [];

//     // 🔍 Look for a matching line by LineId
//     const cookieLine = currentLines.find((line: any) => line.LineId === lineId);

//     if (!cookieLine) {
//         // ✅ LineId not found in baseline → add to RemoveCartLines
//         const newData = getNewCartPosData() || {};
//         const currentRemovals = Array.isArray(newData.RemoveCartLines) ? newData.RemoveCartLines : [];

//         // Avoid duplicates
//         const alreadyExists = currentRemovals.some((line: any) => line.LineId === lineId);

//         if (!alreadyExists) {
//             const updatedData = {
//                 ...newData,
//                 RemoveCartLines: [...currentRemovals, { LineId: lineId }]
//             };

//             console.log('🔹 Added to RemoveCartLines:', updatedData);
//             setNewCartPosData(updatedData);
//         }
//     } else {
//         // ✅ If the line is still in cookie baseline → ensure it is not in RemoveCartLines
//         const newData = getNewCartPosData() || {};
//         const currentRemovals = Array.isArray(newData.RemoveCartLines) ? newData.RemoveCartLines : [];

//         const updatedRemovals = currentRemovals.filter((line: any) => line.LineId !== lineId);

//         const updatedData = {
//             ...newData,
//             RemoveCartLines: updatedRemovals
//         };

//         console.log('🔹 Line still exists → removed from RemoveCartLines:', updatedData);
//         setNewCartPosData(updatedData);
//     }
// };

export const syncCartLineToCookie = (cartLine: any): void => {
    const existingData = getCartData() || {};
    const currentLines = Array.isArray(existingData.cartLines) ? existingData.cartLines : [];

    const cookieLine = currentLines.find((line: any) => line.LineId === cartLine.LineId);

    if (cookieLine) {
        const normalizedCartQty = Number(cartLine.Quantity);
        const normalizedCookieQty = Number(cookieLine.Quantity);

        const normalizedCartComments = cartLine.Comments ?? cartLine.Comment ?? '';
        const normalizedCookieComments = cookieLine.Comments ?? '';

        const isChanged = normalizedCartQty !== normalizedCookieQty || normalizedCartComments !== normalizedCookieComments;

        if (isChanged) {
            console.log('🔹 Found in cookie but changed → updateCartLine');
            updateLineToCartPosData({
                LineId: cartLine.LineId, // ✅ now included
                ItemId: cartLine.ItemId,
                ProductId: cartLine.ProductId,
                InventoryDimensionId: cartLine.InventoryDimensionId,
                Quantity: normalizedCartQty,
                // UnitOfMeasureSymbol: cartLine.UnitOfMeasureSymbol,
                Comments: normalizedCartComments
                // ExtensionProperties: cartLine.ExtensionProperties
            });
        } else {
            console.log('🔹 Matches cookie baseline again → remove from UpdateCartLines');
            removeLineFromUpdateCartPosDataByLineId(cartLine.LineId);
        }
    } else {
        console.log('🔹 Not in cookie → addCartLine', cartLine);
        addLineToCartPosData({
            ItemId: cartLine.ItemId,
            ProductId: cartLine.ProductId,
            InventoryDimensionId: cartLine.InventoryDimensionId,
            Quantity: cartLine.Quantity,
            UnitOfMeasureSymbol: cartLine.UnitOfMeasureSymbol,
            Comments: cartLine.Comments ?? cartLine.Comment ?? '',
            ExtensionProperties: withoutStoreLineFacts(cartLine.ExtensionProperties)
        });
    }
};
